import { Button, H1, H2, Icon, IconName, ListRow, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink, WorkflowLayout } from '@betterment/shared-components';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { urlBuilder } from '../../../Shared/urlBuilder';
import { PURPOSE_TYPES, usePurposeSelectionStep } from '../FlowConfig';

type Props = {};

type Selection = {
  type: (typeof PURPOSE_TYPES)[number];
  title: string;
  description: string;
  icon: IconName;
};

export const SelectionStep: React.FC<Props> = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { commit } = usePurposeSelectionStep('selection');

  const handleNextStep = (purposeType: (typeof PURPOSE_TYPES)[number]) => {
    commit({ purposeType });
    void navigate('/purpose_selection/assignment');
  };

  const selections: Selection[] = [
    {
      type: 'EmergencyFund',
      title: 'Emergency fund',
      description: 'We’ll help you figure out the right amount to save based on your current expenses.',
      icon: 'SafetyNet',
    },
    {
      type: 'Retirement',
      title: 'Retirement',
      description:
        'We’ll help you create a plan so you can retire when you want with enough money for the life you want.',
      icon: 'BeachUmbrella',
    },
    {
      type: 'MajorPurchase',
      title: 'Specific goal',
      description:
        'Save for a specific amount, target date, or goal like building wealth, a home, vacation, education, etc.',
      icon: 'Ribbon',
    },
  ];

  return (
    <WorkflowLayout pageTitle="Set your investing target">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => (returnTo ? navigate(returnTo) : navigate('/'))} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200' }}>
            <H1 css={{ textWrap: 'auto' }}>Do you want to set a goal for this account?</H1>
            <Text color="text.weak">We can recommend a savings plan to help you achieve it.</Text>
          </VStack>
          <VStack css={{ gap: 'space.150' }}>
            {selections.map(({ type, title, description, icon }) => (
              <Button
                key={type}
                onClick={() => handleNextStep(type)}
                variant="ghostSecondary"
                spacing="compact"
                css={{
                  padding: 0,
                  borderStyle: 'solid',
                  borderWidth: 'thin',
                  borderColor: 'border.weak',
                  borderRadius: 'medium',
                }}
              >
                <ListRow css={{ cursor: 'pointer' }}>
                  <ListRow.LeadingAsset css={{ alignSelf: 'start' }}>
                    <Icon name={icon} size="small" />
                  </ListRow.LeadingAsset>
                  <ListRow.LeadingContent>
                    <VStack css={{ display: 'grid' }}>
                      <H2 variant="title100">{title}</H2>
                      <Text>{description}</Text>
                    </VStack>
                  </ListRow.LeadingContent>
                  <ListRow.TrailingAsset>
                    <Icon name="CaretRight" size="small" />
                  </ListRow.TrailingAsset>
                </ListRow>
              </Button>
            ))}
          </VStack>
          <SambaAutoLink to={urlBuilder.appAddGoalFlowsNew.path} variant="solidPrimary" css={{ width: 'fit-content' }}>
            Skip for now
          </SambaAutoLink>
        </VStack>
      </WorkflowLayout.Content>
    </WorkflowLayout>
  );
};
